import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/Button.tsx'
import { IconExternalLink, IconPencil, IconPlus, IconSearch, IconSettings, IconTrash } from '@tabler/icons-react'
import { Input } from 'react-daisyui'
import { Link, useNavigate } from 'react-router-dom'
import { useClient } from '@/stores/useClient.ts'
import { ProjectsProvider, useProjectsContext } from '@/contexts/projectsContext.tsx'
import MyTable, { MyTableColumn } from '@/components/portal/MyTable.tsx'
import PortalStandardComponent from '@/components/portal/PortalStandardComponent.tsx'
import { CreateProjectModal, useCreateProjectModal } from '@/components/portal/CreateProjectModal.tsx'
import { ProjectLimitModal, useProjectLimitModal, isProjectLimitError } from '@/components/portal/ProjectLimitModal.tsx'
import { DeleteConfirmModal, useDeleteConfirmModal } from '@/components/portal/DeleteConfirmModal.tsx'
import { ProfProject } from '@/models/profProject.ts'
import { getThumbnailFromAlbums } from '@/models/album.ts'
import { IconPhoto } from '@tabler/icons-react'
import TagBadge from '@/components/portal/TagBadge.tsx'
import { TagManagementModal, useTagManagementModal } from '@/components/portal/TagManagementModal.tsx'
import TagEditDialog, { useTagEditDialog } from '@/components/portal/TagEditDialog.tsx'
import TutorialSpot from '@/components/tutorial/TutorialSpot.tsx'
import { useTutorial } from '@/contexts/tutorialContext.tsx'

export default function ProjectsPage() {
  return (
    <ProjectsProvider>
      <ProjectsPageContent />
    </ProjectsProvider>
  )
}

function ProjectsPageContent() {
  const navigate = useNavigate()
  const tutorialIsActive = useTutorial(s => s.isActive)
  const tutorialSeries = useTutorial(s => s.activeSeries)
  const tutorialStepIndex = useTutorial(s => s.currentStepIndex)
  const tutorialAdvance = useTutorial(s => s.advanceStep)
  const projectsContext = useProjectsContext()!
  const { projects, totalCount, isLoading, params, setSort, setPage, setSearch, allTags, refetch } = projectsContext
  const { createProject, deleteProject, updateTag } = useClient()
  const { modalRef: createModalRef, show: showCreateModal, close: closeCreateModal } = useCreateProjectModal()
  const { modalRef: limitModalRef, show: showLimitModal } = useProjectLimitModal()
  const { modalRef: deleteModalRef, show: showDeleteModal, close: closeDeleteModal } = useDeleteConfirmModal()
  const { modalRef: tagManageModalRef, show: showTagManageModal } = useTagManagementModal()
  const tagEdit = useTagEditDialog()
  const queryClient = useQueryClient()
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<ProfProject | null>(null)
  const [searchInput, setSearchInput] = useState(params.search || '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput)
    }, 280)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchInput, setSearch])

  const goToProject = (project: ProfProject) => navigate(`/portal/projects/${project.id}`)

  const columns: MyTableColumn<ProfProject>[] = [
    {
      key: 'thumbnail',
      label: '',
      className: 'w-16',
      onCellClick: goToProject,
      render: (project) => {
        const thumb = getThumbnailFromAlbums(project.drive.albums)
        return (
          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center overflow-hidden flex-shrink-0">
            {thumb ? (
              <img src={thumb} alt={project.title} className="w-full h-full object-cover" />
            ) : (
              <IconPhoto size={16} className="opacity-40" />
            )}
          </div>
        )
      },
    },
    {
      key: 'title',
      label: 'Name',
      sortKey: 'title',
      className: 'w-[40%]',
      onCellClick: goToProject,
      render: (project) => {
        const isNewest = projects.length > 0 && projects[0].id === project.id
        const isTutorialWaiting = tutorialIsActive && tutorialSeries?.steps[tutorialStepIndex]?.spotId === 'projects-newest-row'
        const title = (
          <span className="title-hover-target">
            {project.title}
          </span>
        )
        if (isNewest && isTutorialWaiting) {
          return <TutorialSpot id="projects-newest-row">{title}</TutorialSpot>
        }
        return title
      },
    },
    {
      key: 'tags',
      label: 'Tags',
      render: (project) => (
        <div className="flex flex-wrap gap-1">
          {project.tags.map(t => <TagBadge key={t.id} tag={t} size="sm" onColorClick={() => tagEdit.open(t)} />)}
        </div>
      ),
    },
    {
      key: 'event_date',
      label: 'Datum',
      sortKey: 'event_date',
      render: (project) => (
        <span className="opacity-50">
          {project.drive.event_date
            ? new Date(project.drive.event_date).toLocaleDateString('de-DE')
            : '–'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Erstellt',
      sortKey: 'created_at',
      render: (project) => (
        <span className="opacity-50">{new Date(project.created_at).toLocaleDateString('de-DE')}</span>
      ),
    },
  ]

  async function handleEditTagSave(tagId: string, data: { name: string, color: string }) {
    await updateTag(tagId, data)
    queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  async function handleCreateProject(title: string, templateId: string | null) {
    setIsCreating(true)
    try {
      await createProject(title, templateId)
      closeCreateModal()
      refetch()
      // Advance tutorial after project creation (step 1 "wait" → step 2 "newest row")
      if (tutorialIsActive && tutorialSeries?.steps[tutorialStepIndex]?.spotId === 'projects-new-button') {
        tutorialAdvance()
      }
    } catch (e: any) {
      console.error('Fehler beim Erstellen des Projekts:', e)
      if (isProjectLimitError(e)) {
        closeCreateModal()
        showLimitModal()
      } else {
        alert('Fehler beim Erstellen des Projekts')
      }
    } finally {
      setIsCreating(false)
    }
  }

  function handleDeleteClick(project: ProfProject) {
    setProjectToDelete(project)
    showDeleteModal()
  }

  async function handleDeleteConfirm() {
    if (!projectToDelete) return
    setIsDeleting(true)
    try {
      await deleteProject(projectToDelete.id)
      closeDeleteModal()
      setProjectToDelete(null)
      refetch()
    } catch (e: any) {
      console.error('Fehler beim Löschen des Projekts:', e)
      alert('Fehler beim Löschen des Projekts')
    } finally {
      setIsDeleting(false)
    }
  }

  const totalPages = Math.ceil(totalCount / params.page_size)

  return (
    <PortalStandardComponent
      title="Projekte"
      description="Erstelle Galerien mit Fotos und Videos, die du deinen Kunden zur Verfügung stellst."
      noCard
      actions={
        <>
          {allTags.length > 0 && (
            <Button
              size='sm'
              color='ghost'
              onClick={showTagManageModal}
              title="Tags verwalten"
            >
              <IconSettings size={16} />
              Tags verwalten
            </Button>
          )}
          <TutorialSpot id="projects-new-button">
            <Button color="accent" onClick={showCreateModal}>
              <IconPlus size={20} />
              Neues Projekt
            </Button>
          </TutorialSpot>
        </>
      }
    >
      <div className="mb-6 relative max-w-md">
        <IconSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
        <Input
          className="pl-10 w-full bg-neutral rounded-full"
          placeholder="Projekt suchen..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
      </div>

      <div className="bg-neutral border border-[var(--portal-border)] p-3 md:p-6">
        <MyTable
        columns={columns}
        data={projects}
        currentSort={params.o}
        onSortChange={setSort}
        isLoading={isLoading}
        emptyText="Noch keine Projekte vorhanden"
        actions={(project) => (
          <div className="flex gap-0 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
            <a href={`/d/${project.drive.id}`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100" title="Drive öffnen">
                <IconExternalLink size={16} />
              </Button>
            </a>
            <Link to={`/portal/projects/${project.id}`}>
              <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100" title="Bearbeiten">
                <IconPencil size={16} />
              </Button>
            </Link>
            <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100 hover:text-error" title="Löschen" onClick={() => handleDeleteClick(project)}>
              <IconTrash size={16} />
            </Button>
          </div>
        )}
      />
      </div>

      {totalCount > 0 && (
        <p className="text-sm opacity-50 mt-3">{totalCount} {totalCount === 1 ? 'Projekt' : 'Projekte'}</p>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            size="sm"
            disabled={params.page <= 1}
            onClick={() => setPage(params.page - 1)}
          >
            Zurück
          </Button>
          <span className="flex items-center px-3 text-sm">
            Seite {params.page} von {totalPages}
          </span>
          <Button
            size="sm"
            disabled={params.page >= totalPages}
            onClick={() => setPage(params.page + 1)}
          >
            Weiter
          </Button>
        </div>
      )}

      <CreateProjectModal
        modalRef={createModalRef}
        onConfirm={handleCreateProject}
        isLoading={isCreating}
      />

      <ProjectLimitModal modalRef={limitModalRef} />

      <DeleteConfirmModal
        modalRef={deleteModalRef}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        title="Projekt löschen"
        message={`Möchtest du das Projekt „${projectToDelete?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
      />

      <TagManagementModal modalRef={tagManageModalRef} />

      <TagEditDialog
        modalRef={tagEdit.modalRef}
        tag={tagEdit.tag}
        onSave={handleEditTagSave}
      />
    </PortalStandardComponent>
  )
}
